import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, PageHeader, Table } from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { useParams } from 'react-router'
import { formatCurrency, formatNumber } from '../../../../../lib/utils/formatNumber'
import { createColumnHelper } from '@tanstack/react-table'
import PolarAreaChart from '../../../../../components/Charts/PolarAreaChart'
import { useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { useGetDisplayCampaignById } from '../../../../../api/dealership/campaigns/useGetDisplayCampaignById'
import { DisplayCreativeMetrics } from '../../../../../lib/types/DisplayCreativeMetrics'
import { useGetDisplayCreativeMetrics } from '../../../../../api/dealership/campaigns/reports/useGetDisplayCreativeMetrics'
import { DateRange } from '../../../../../lib/types/DateRange'
import HorizontalBarChart from '../../../../../components/Charts/HorizontalBarChart'
import BarChart from '../../../../../components/Charts/BarChart'
import ConversionAttributionMetrics from '../../../../../components/Campaigns/Reports/ConversionAttributionMetrics'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import ContentMetrics from '../../../../../components/Campaigns/Reports/ContentMetrics'
import { over, take } from 'lodash'
import TopPerformingCreatives from '../../../../../components/Campaigns/Reports/TopPerformingCreatives'
import { useGetOTTReport } from '../../../../../api/dealership/campaigns/reports/useGetOTTReport'
import { useGetCampaignBudget } from '../../../../../api/dealership/campaigns/useGetCampaignBudget'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import SiteListTable from '../../../../../components/Campaigns/Reports/SiteListWithAdSizeBreakout'

const columnHelper = createColumnHelper<DisplayCreativeMetrics>()

const AudienceCampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { data: campaignBudget } = useGetCampaignBudget({
    campaign_id,
    filters: {
      start_date: filters?.dateRange.startDate,
      end_date: filters?.dateRange.endDate,
    },
  })
  const {
    data: {
      overallMetrics = {
        impressions: 0,
        clicks: 0,
        video_completions: 0,
        conversions: 0,
        household_uniques: 0,
        household_frequency: 0,
        vtr: 0,
        reach: 0,
        frequency: 0,
      },
    } = {},
    isLoading: isLoadingReport,
  } = useGetOTTReport({
    campaign_id: campaign_id,
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    if (!isLoadingReport) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoadingReport])

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={'../'}
        title={`${dealership?.dealership} - ${
          campaign?.client_campaign_display_name ?? ''
        }`}
        {...(Boolean(agency?.show_budget_type) && {
          infoPill: {
            type: 'warning',
            content: `Budget: ${
              agency?.show_budget_type === 1
                ? formatCurrency(campaignBudget?.demand_local_net)
                : formatCurrency(campaignBudget?.gross_revenue)
            }`,
          },
        })}
        cardsOptions={[
          {
            title: 'VTR',
            content: `${Math.round(10 * overallMetrics?.vtr) / 10}%`,
            color: 'bg-palatinate-blue/30',
          },
          {
            title: 'Unique Households',
            content: formatNumber(overallMetrics?.reach ?? 0),
            color: 'bg-kelly-green/30',
            info: 'Total number of unique households who are exposed to an ad',
          },
          {
            title: 'Household Frequency',
            content: formatNumber(overallMetrics?.frequency),
            color: 'bg-secondary-malachite-green/30',
            info: 'Average number of times a household was exposed to an ad, this number is calculated over monthly data only.',
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isLoadingReport || isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'OTT',
                    campaign_id: campaign?.client_campaign_id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'OTT', campaign?.client_campaign_id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.client_campaign_display_name}_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-10'} />
        {children}
      </div>
    </div>
  )
}

const OTTCampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const { dateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const {
    data: { metrics } = { metrics: [] },
    data: {
      overallMetrics = {
        impressions: 0,
        clicks: 0,
        first_quartile_completions: 0,
        second_quartile_completions: 0,
        third_quartile_completions: 0,
        video_completions: 0,
        video_starts: 0,
        vtr: 0,
      },
    } = {},
    data: { deviceData } = { deviceData: [] },
    data: { deviceTypeData } = { deviceTypeData: [] },
    data: { premiumPublishers } = { premiumPublishers: [] },
    data: { geoInsightsCityData } = { geoInsightsCityData: [] },
    isLoading: isLoadingReport,
    isPreviousData: isPreviousReportsData,
  } = useGetOTTReport({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const creativeMetricsColumns = useMemo(
    () => [
      columnHelper.accessor('creative_name', {
        header: 'Creative Name',
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
      }),

      columnHelper.accessor('views', {
        header: 'Views',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
      }),

      columnHelper.display({
        header: () => 'VTR',
        id: 'vtr',
        cell: ({ row }) => `${Math.round((row.original.vtr ?? 0) * 1000) / 10}%`,
      }),
    ],
    [],
  )

  return (
    <AudienceCampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'grid grid-cols-1 gap-8'}>
          <div className={'grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Impressions</span>
                <span>
                  {!isLoadingReport &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map((metric: { impressions: number }) => metric.impressions)
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'green-to-cyan'}
                  yKey={'impressions'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Video Completions</span>
                <span>
                  {!isLoadingReport &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map(
                          (metric: { video_completions: number }) =>
                            metric.video_completions,
                        )
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'red-to-orange'}
                  yKey={'video_completions'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
          </div>
          <ConversionAttributionMetrics filters={filters} />
          <div className={'grid grid-cols-2 justify-center gap-5'}>
            {!!campaign?.show_sites && <ContentMetrics filters={filters} />}
            {!isLoadingReport && deviceData?.length > 0 && (
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Impressions by Device Make</span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <PolarAreaChart
                    isPreviousData={isPreviousReportsData}
                    isLoading={isLoadingReport}
                    data={
                      deviceData?.map(
                        (data: { device_make: string; impressions: number }) => {
                          return {
                            label: data.device_make,
                            value: data.impressions,
                          }
                        },
                      ) ?? []
                    }
                  />
                </div>
              </div>
            )}
            {!isLoadingReport && deviceData?.length > 0 && (
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>Impressions by Device Type</span>
                </div>
                <div className={'h-[270px] p-2'}>
                  <PolarAreaChart
                    isPreviousData={isPreviousReportsData}
                    isLoading={isLoadingReport}
                    data={
                      deviceTypeData?.map(
                        (data: { device_type: string; impressions: number }) => {
                          return {
                            label: data.device_type,
                            value: data.impressions,
                          }
                        },
                      ) ?? []
                    }
                  />
                </div>
              </div>
            )}
            {!!campaign?.show_geo &&
              !isLoadingReport &&
              geoInsightsCityData?.length > 0 && (
                <div className={'min-h-[270px] border-2 rounded-xl'}>
                  <div
                    className={
                      'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                    }>
                    <span>Impressions by City</span>
                  </div>
                  <div className={'h-[270px] p-2'}>
                    <PolarAreaChart
                      isPreviousData={isPreviousReportsData}
                      isLoading={isLoadingReport}
                      data={
                        take(
                          geoInsightsCityData
                            ?.filter((d) => d.type === 'city')
                            ?.map(
                              (data: { location_name: string; impressions: number }) => {
                                return {
                                  label: data.location_name,
                                  value: data.impressions,
                                }
                              },
                            ),
                          5,
                        ) ?? []
                      }
                    />
                  </div>
                </div>
              )}
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Quartile Views</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <BarChart
                  data={[
                    {
                      label: '25% Completion',
                      value:
                        Math.round(
                          (overallMetrics?.first_quartile_completions * 10000) /
                            overallMetrics?.video_starts,
                        ) / 100,
                    },
                    {
                      label: '50% Completion',
                      value:
                        Math.round(
                          (overallMetrics?.second_quartile_completions * 10000) /
                            overallMetrics?.video_starts,
                        ) / 100,
                    },
                    {
                      label: '75% Completion',
                      value:
                        Math.round(
                          (overallMetrics?.third_quartile_completions * 10000) /
                            overallMetrics?.video_starts,
                        ) / 100,
                    },
                    {
                      label: '100% Completion',
                      value:
                        Math.round(
                          (overallMetrics?.video_completions * 10000) /
                            overallMetrics?.video_starts,
                        ) / 100,
                    },
                  ]}
                  isLoading={false}
                  customOptions={{
                    datalabels: {
                      formatter: (value: number): string => `${value}%`,
                    },
                  }}
                />
              </div>
            </div>
            {!campaign?.show_geo && metrics.length > 0 && (
              <div className={'min-h-[270px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                  }>
                  <span>VTR</span>
                  <div className={'flex items-center gap-2'}>
                    {Math.round(overallMetrics?.vtr * 10) / 10}%
                  </div>
                </div>
                <div className={'h-[270px] p-2'}>
                  <LineChart
                    data={metrics ?? []}
                    gradient={'blue-to-cyan'}
                    yKey={'vtr'}
                    isLoading={isLoadingReport}
                    isPreviousData={isPreviousReportsData}
                  />
                </div>
              </div>
            )}
          </div>
          {!isLoadingReport && premiumPublishers?.length > 0 && (
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Premium Publishers</span>
              </div>
              <div className={'h-[270px] p-2'}>
                <HorizontalBarChart
                  isPreviousData={isPreviousReportsData}
                  isLoading={isLoadingReport}
                  data={
                    premiumPublishers?.map(({ site_name, impressions }) => {
                      return {
                        label: site_name,
                        value: impressions,
                      }
                    }) ?? []
                  }
                />
              </div>
            </div>
          )}
          {!!campaign?.show_geo && metrics.length > 0 && (
            <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>VTR</span>
                <div className={'flex items-center gap-2'}>
                  {Math.round(overallMetrics?.vtr * 10) / 10}%
                </div>
              </div>
              <div className={'w-full h-[320px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'blue-to-cyan'}
                  yKey={'vtr'}
                  isLoading={isLoadingReport}
                  isPreviousData={isPreviousReportsData}
                />
              </div>
            </div>
          )}
          <TopPerformingCreatives filters={filters} creativeType="video" />
          {!!campaign?.show_site_list && (
            <div className={'flex flex-col gap-4'}>
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Top Performing Sites
              </span>
              <SiteListTable />
            </div>
          )}
          <div className={'flex flex-col gap-4'}>
            <span className={'font-bold text-primary-xiketic text-sm'}>
              Top Performing Creatives
            </span>
            <Table
              emptyLabel={'No creatives found'}
              columns={creativeMetricsColumns}
              filters={{
                campaign_id: campaign_id,
                start_date: filters?.dateRange?.startDate,
                end_date: filters?.dateRange?.endDate,
                min_impressions: 1,
                limit: 10,
              }}
              defaultSorting={[{ id: 'impressions', desc: true }]}
              fetchFn={useGetDisplayCreativeMetrics}
              isDownloadable={true}
            />
          </div>
        </div>
      </div>
    </AudienceCampaignTemplate>
  )
}

export default OTTCampaign
